import { memo, useCallback, useMemo } from 'react';

import { Typography, Banner, Accordion, TreeList } from '@scale/scaleui';

import { InteractionStepType } from '@scale/shared/types/chat';

import NumberInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/NumberInputBuilder';
import BooleanInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/BooleanInputBuilder';
import StringInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/StringInputBuilder';

import { LikertScale, getDefaultLikerLabelForDistance } from '../LikertScale';
import { StepConfigurationComponentProps } from '../../stepDescriptor/types';

function LikertScaleConfig({
  params: currentParams,
  setParams: setCurrentParams,
}: Omit<
  StepConfigurationComponentProps<InteractionStepType.ModelResponseSelector>,
  'property' | 'previousParams'
>) {
  const { params } = currentParams;

  const showLikertScale = params.show_likert_scale;
  const minLikert = params.min_likert || 1;
  const maxLikert = params.max_likert || 7;

  const setShowLikerScale = useCallback(
    (value: boolean) => {
      setCurrentParams({
        ...currentParams,
        params: { ...currentParams.params, show_likert_scale: value },
      });
    },
    [setCurrentParams, currentParams],
  );

  const distances = useMemo(() => {
    const distance = Math.abs(maxLikert - minLikert);
    const distances = distance % 2 === 0 ? distance / 2 : (distance + 1) / 2;
    return Array.from({ length: distances + 1 }, (_, i) => i);
  }, [minLikert, maxLikert]);

  return (
    <div className="w-full">
      <BooleanInputBuilder
        name={'Use Likert Scale (1-7 scale to indicate which response is better)'}
        existingValue={!!showLikertScale}
        updateProperty={setShowLikerScale}
      />
      {showLikertScale && (
        <TreeList childContainerClassName="w-full max-w-none">
          <NumberInputBuilder
            name={'Min Likert Value (default: 1)'}
            existingValue={minLikert}
            updateProperty={(value: number) => {
              if (!!params.max_likert && value >= params.max_likert) {
                setCurrentParams({
                  ...currentParams,
                  params: {
                    ...currentParams.params,
                    min_likert: currentParams.params.max_likert,
                    max_likert: value + 1,
                  },
                });
              } else {
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, min_likert: value },
                });
              }
            }}
          />
          <NumberInputBuilder
            name={'Max Likert Value (default: 7)'}
            existingValue={maxLikert}
            updateProperty={(value: number) => {
              if (!!currentParams.params.min_likert && value <= currentParams.params.min_likert) {
                setCurrentParams({
                  ...currentParams,
                  params: {
                    ...currentParams.params,
                    max_likert: currentParams.params.min_likert,
                    min_likert: value - 1,
                  },
                });
              } else {
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, max_likert: value },
                });
              }
            }}
            // @ts-ignore
            className="w-[40px]"
          />
          <Accordion header="[Optional] Custom Likert Labels">
            <Banner
              title="Info"
              variant="info"
              subtitle="You can use {better} or {worse} as template variables"
            />
            {distances.map(distance => (
              <StringInputBuilder
                key={distance}
                name={distance === 0 ? 'No Preference' : `Distance ${distance}`}
                placeholder={getDefaultLikerLabelForDistance(distance)}
                existingValue={currentParams.params.custom_likert_labels?.at(distance) ?? ''}
                updateProperty={(value: string) => {
                  const distanceLabels = currentParams.params.custom_likert_labels ?? [];
                  distanceLabels[distance] = value;
                  setCurrentParams({
                    ...currentParams,
                    params: {
                      ...currentParams.params,
                      custom_likert_labels: distanceLabels,
                    },
                  });
                }}
              />
            ))}

            <Typography.Subtitle3>Preview:</Typography.Subtitle3>
            <div className="bg-neutral-100 rounded-lg p-3">
              <LikertScale
                onSelect={() => {}}
                selectedLikertValue={null}
                readOnly
                minLikert={currentParams.params.min_likert || 1}
                maxLikert={currentParams.params.max_likert || 7}
                customLabels={currentParams.params.custom_likert_labels}
              />
            </div>
          </Accordion>
        </TreeList>
      )}
    </div>
  );
}

export default memo(LikertScaleConfig);
